import { useEffect, useState } from 'react';
import axios from 'axios';
import { useDocumentTitle } from 'usehooks-ts';
import { PageHeader } from '@tailwindComponents/PageHeader';
import { FloxSpinner } from '@tailwindComponents/FloxSpinner';
import { useAuthHttp } from '@hooks';
import {
  INTEGRATIONS_GET_URL,
  ADD_INTEGRATION_URL,
  REMOVE_INTEGRATION_URL,
} from '@constants';
import { INTEGRATIONS, IntegrationConfig } from './integrationsConfig';
import {
  IntegrationCredential,
  buildCredentialPayload,
  connectedCredentialsByPlatform,
  integrationsToShow,
  isAvailableToConnect,
} from './integrationsHelpers';
import { IntegrationCard } from './IntegrationCard';
import { ConnectIntegrationModal } from './ConnectIntegrationModal';

/** HTTP status of an Axios error, or undefined — never the (secret-bearing) error object. */
const statusOf = (error: unknown): number | undefined =>
  axios.isAxiosError(error) ? error.response?.status : undefined;

export function Integrations() {
  const authHttp = useAuthHttp();
  useDocumentTitle('FLOX - Integrations');

  const [isLoading, setIsLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [connectedCredentials, setConnectedCredentials] = useState<
    Map<string, IntegrationCredential>
  >(new Map());
  const [availablePlatforms, setAvailablePlatforms] = useState<unknown>();
  const [selectedConfig, setSelectedConfig] =
    useState<IntegrationConfig | null>(null);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [busyKey, setBusyKey] = useState<string | null>(null);

  const fetchIntegrations = async () => {
    try {
      const { data } = await authHttp.get(INTEGRATIONS_GET_URL);
      setConnectedCredentials(
        connectedCredentialsByPlatform(data?.credentials)
      );
      setAvailablePlatforms(data?.availablePlatforms);
    } catch (error) {
      const status = statusOf(error);
      if (status === 401 || status === 403) setAccessDenied(true);
      // eslint-disable-next-line no-console
      console.error('Failed to load integrations', status);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchIntegrations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleConnectClick = (config: IntegrationConfig) => {
    setSubmitError('');
    setSelectedConfig(config);
    setIsReconnecting(connectedCredentials.has(config.key));
    setIsModalOpen(true);
  };

  const handleSubmit = async (values: Record<string, string>) => {
    if (!selectedConfig) return;
    setIsSubmitting(true);
    setSubmitError('');
    try {
      await authHttp.post(
        ADD_INTEGRATION_URL,
        buildCredentialPayload(selectedConfig, values)
      );
      setIsModalOpen(false);
      await fetchIntegrations();
    } catch (error) {
      setSubmitError(
        'Could not connect. Please check your details and try again.'
      );
      // eslint-disable-next-line no-console
      console.error('AddIntegration failed', statusOf(error));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDisconnect = async (config: IntegrationConfig) => {
    setBusyKey(config.key);
    try {
      await authHttp.post(REMOVE_INTEGRATION_URL, {
        IntegrationName: config.key,
      });
      await fetchIntegrations();
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('RemoveIntegration failed', statusOf(error));
    } finally {
      setBusyKey(null);
    }
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className='flex justify-center py-16'>
          <FloxSpinner />
        </div>
      );
    }
    if (accessDenied) {
      return (
        <p className='text-sm text-gray-500'>
          You need administrator access to manage integrations. Please contact
          your business admin.
        </p>
      );
    }
    return (
      <div className='flex flex-wrap gap-4'>
        {integrationsToShow(
          INTEGRATIONS,
          availablePlatforms,
          connectedCredentials
        ).map((config) => (
          <IntegrationCard
            key={config.key}
            config={config}
            connectedCredential={connectedCredentials.get(config.key)}
            isBusy={busyKey === config.key}
            isReconnectAvailable={isAvailableToConnect(
              config.key,
              availablePlatforms
            )}
            onConnect={() => handleConnectClick(config)}
            onDisconnect={() => handleDisconnect(config)}
          />
        ))}
      </div>
    );
  };

  return (
    <>
      <PageHeader
        header='Integrations'
        subHeader='Connect your tools and services to sync data with Flox.'
        pages={[{ name: 'Integrations', href: '/integrations', current: true }]}
      />
      <main className='flex flex-col gap-6 p-4 pb-16 lg:px-8 lg:pb-24'>
        {renderContent()}
      </main>

      <ConnectIntegrationModal
        config={selectedConfig}
        isOpen={isModalOpen}
        isReconnecting={isReconnecting}
        setIsOpen={setIsModalOpen}
        isSubmitting={isSubmitting}
        submitError={submitError}
        onSubmit={handleSubmit}
      />
    </>
  );
}
